import { api, unwrap } from "@lumberset/api-client";
import { money } from "@lumberset/takeoff-ui";
import {
  Badge,
  Button,
  Container,
  Drawer,
  EmptyState,
  Field,
  Input,
  SectionHeader,
  Select,
  Table,
  toast,
} from "@lumberset/ui";
import { ArrowUpTray, MagnifyingGlass, Plus, Tag } from "@medusajs/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type FormEvent, useDeferredValue, useRef, useState } from "react";
import { Crumbs } from "../../components/crumbs";
import { readCatalogCsv } from "../../lib/csv";
import { catalogQuery } from "../../lib/queries";

export const Route = createFileRoute("/_app/catalog")({ component: Catalog });

const matchKinds: Record<string, string> = {
  sawn: "Lumber",
  panel: "Panel",
  engineered: "Engineered",
  hardware: "Hardware",
};

function Catalog() {
  const queryClient = useQueryClient();
  const { yard } = Route.useRouteContext();
  const canEdit = yard.role !== "member";
  const file = useRef<HTMLInputElement>(null);

  const [text, setText] = useState("");
  const [page, setPage] = useState(0);
  const [adding, setAdding] = useState(false);
  const q = useDeferredValue(text);
  const { data } = useQuery(catalogQuery({ q: q || undefined, page }));

  const importRows = useMutation({
    mutationFn: async (chosen: File) => {
      const parsed = readCatalogCsv(await chosen.text());
      if (parsed.problem) throw new Error(parsed.problem);
      const result = await unwrap(api.catalog.import.$post({ json: { items: parsed.rows } }));
      return { ...result, skipped: parsed.skipped };
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["catalog"] });
      toast.success(`${result.written} items loaded`, {
        description: `${result.recognised} recognized for automatic matching.${
          result.skipped ? ` ${result.skipped} rows had no SKU or description.` : ""
        }`,
      });
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <>
      <Crumbs trail={[{ label: "Catalog" }]} />
      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title="Catalog"
          description="What your yard sells, at your prices. Takeoff lines are matched to these items when you create a quote."
          actions={
            canEdit ? (
              <>
                <input
                  ref={file}
                  type="file"
                  accept=".csv,text/csv"
                  hidden
                  onChange={(event) => {
                    const chosen = event.target.files?.[0];
                    if (chosen) importRows.mutate(chosen);
                    event.target.value = "";
                  }}
                />
                <Button
                  size="small"
                  variant="secondary"
                  isLoading={importRows.isPending}
                  onClick={() => file.current?.click()}
                >
                  <ArrowUpTray /> Import CSV
                </Button>
                <Button size="small" onClick={() => setAdding(true)}>
                  <Plus /> Add item
                </Button>
              </>
            ) : null
          }
        />

        <div className="px-6 py-3 sm:max-w-xs">
          <Input
            type="search"
            placeholder="Search SKU or description"
            prefix={<MagnifyingGlass />}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              setPage(0);
            }}
          />
        </div>

        {data && data.count === 0 ? (
          <EmptyState
            icon={<Tag />}
            title={q ? "Nothing matches" : "Your catalog is empty"}
            description={
              q
                ? "Try a SKU or part of a description."
                : "Export a price list from your point of sale as CSV with SKU, Description, Unit and Price columns, then import it here. Re-import any time prices change."
            }
          />
        ) : (
          <>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>SKU</Table.HeaderCell>
                  <Table.HeaderCell>Description</Table.HeaderCell>
                  <Table.HeaderCell className="max-md:hidden">Matches as</Table.HeaderCell>
                  <Table.HeaderCell className="max-sm:hidden">Unit</Table.HeaderCell>
                  <Table.HeaderCell className="text-right">Price</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {data?.items.map((item) => (
                  <Table.Row key={item.id}>
                    <Table.Cell className="code-label whitespace-nowrap text-ui-fg-base">
                      {item.sku}
                    </Table.Cell>
                    <Table.Cell className="text-ui-fg-base">{item.description}</Table.Cell>
                    <Table.Cell className="max-md:hidden">
                      {item.matchKey?.kind &&
                      (item.matchKey.kind !== "hardware" || item.matchKey.model) ? (
                        <Badge>{matchKinds[item.matchKey.kind] ?? item.matchKey.kind}</Badge>
                      ) : (
                        <span className="text-ui-fg-muted">Manual only</span>
                      )}
                    </Table.Cell>
                    <Table.Cell className="max-sm:hidden">{item.unit}</Table.Cell>
                    <Table.Cell className="tabular whitespace-nowrap text-right text-ui-fg-base">
                      {item.priceCents !== null
                        ? money(item.priceCents)
                        : item.costCents !== null
                          ? `${money(item.costCents)} cost`
                          : "-"}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            {data ? (
              <Table.Pagination
                page={page}
                pageSize={data.pageSize}
                count={data.count}
                onPageChange={setPage}
              />
            ) : null}
          </>
        )}
      </Container>

      <AddItem open={adding} onClose={() => setAdding(false)} />
    </>
  );
}

function AddItem({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [unit, setUnit] = useState("ea");

  const add = useMutation({
    mutationFn: (form: FormData) =>
      unwrap(
        api.catalog.$post({
          json: {
            sku: String(form.get("sku")).trim(),
            description: String(form.get("description")).trim(),
            unit: unit as "ea",
            priceCents: Math.round(Number(form.get("price")) * 100),
          },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["catalog"] });
      toast.success("Item added");
      onClose();
    },
    onError: (error) => toast.error(error.message),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    add.mutate(new FormData(event.currentTarget));
  }

  return (
    <Drawer open={open} onOpenChange={(next) => !next && onClose()}>
      <Drawer.Content>
        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <Drawer.Header>
            <Drawer.Title>Add catalog item</Drawer.Title>
            <Drawer.Description>
              Write the description the way your yard does, for example "2x6x16 DF #2". Size, length
              and treatment are read from it for matching.
            </Drawer.Description>
          </Drawer.Header>
          <Drawer.Body className="flex flex-col gap-y-5">
            <Field label="SKU">
              {(control) => <Input {...control} name="sku" required autoFocus />}
            </Field>
            <Field label="Description">
              {(control) => <Input {...control} name="description" required />}
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Unit">
                {(control) => (
                  <Select value={unit} onValueChange={setUnit}>
                    <Select.Trigger id={control.id}>
                      <Select.Value />
                    </Select.Trigger>
                    <Select.Content>
                      <Select.Item value="ea">Each</Select.Item>
                      <Select.Item value="lf">Linear foot</Select.Item>
                      <Select.Item value="sheet">Sheet</Select.Item>
                      <Select.Item value="bf">Board foot</Select.Item>
                      <Select.Item value="box">Box</Select.Item>
                    </Select.Content>
                  </Select>
                )}
              </Field>
              <Field label="Price">
                {(control) => (
                  <Input
                    {...control}
                    name="price"
                    inputMode="decimal"
                    prefix="$"
                    required
                    pattern="[0-9]*\.?[0-9]{0,2}"
                  />
                )}
              </Field>
            </div>
          </Drawer.Body>
          <Drawer.Footer>
            <Button type="button" variant="secondary" size="small" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="small" isLoading={add.isPending}>
              Add item
            </Button>
          </Drawer.Footer>
        </form>
      </Drawer.Content>
    </Drawer>
  );
}
